import type { TFunction } from 'i18next';

import type { OralExamReadiness as OralExamReadinessType } from '@/common/api/models/oralExamReadiness';
import { OralExamReadiness } from '@/common/api/models/oralExamReadiness';
import { formatEnglishTime } from '@/common/utils/formatEnglishDateTime';

import {
  canReviewIdInPreflight,
  readinessStatusTestId,
  shouldShowPreflightIdPhotos,
} from '../StudentLiveTile';

import type { IdConsentReviewItem } from './types';

export type BandKind = 'review' | 'waiting' | 'rejected' | 'approved' | 'registered';

/** The dot carries the state's colour; status text is always body text. */
export type BandTone = {
  dotColor: string;
};

export type BandState = {
  kind: BandKind;
  statusLabel: string;
  statusColor: string;
  statusTestId: string;
  tone: BandTone;
  readinessLabel?: string;
};

export function isRegisteredBand(readiness: OralExamReadinessType): boolean {
  return readiness === OralExamReadiness.not_joined
    || readiness === OralExamReadiness.not_connected
    || readiness === OralExamReadiness.presence_unknown;
}

function toneFromStatusColor(statusColor: string): BandTone {
  if (statusColor === '$statusWarning') {
    return { dotColor: '$statusWarning' };
  }
  if (statusColor === '$statusSuccess') {
    return { dotColor: '$statusSuccess' };
  }
  if (statusColor === '$textSubtle') {
    return { dotColor: '$textHint' };
  }
  return { dotColor: '$textHint' };
}

export function resolveBandState(
  item: IdConsentReviewItem,
  t: TFunction,
): BandState {
  const { readiness } = item;
  const isApproved = item.idPhotoStatus === 'approved';
  const hasConsent = Boolean(item.consentRecordedAt);
  const hasPhoto = item.idPhotoStatus === 'submitted' || isApproved;
  const neitherConsentNorPhoto = !hasConsent && !hasPhoto;

  if (isRegisteredBand(readiness)) {
    let statusLabel: string;
    const statusColor = '$textSubtle';
    if (readiness === OralExamReadiness.not_connected) {
      statusLabel = t('oralExam.lecturer.readiness.notConnected', {
        defaultValue: 'Disconnected — not connected',
      });
    } else if (readiness === OralExamReadiness.presence_unknown) {
      statusLabel = t('oralExam.lecturer.readiness.presenceUnknown', {
        defaultValue: 'Registered — not joined yet',
      });
    } else {
      statusLabel = t('oralExam.lecturer.readiness.notJoined', {
        defaultValue: 'Registered — not joined yet',
      });
    }
    return {
      kind: 'registered',
      statusLabel,
      statusColor,
      statusTestId: readinessStatusTestId(item.bookingId, readiness),
      tone: toneFromStatusColor(statusColor),
    };
  }

  if (isApproved && canReviewIdInPreflight(readiness)) {
    let readinessLabel: string;
    if (readiness === OralExamReadiness.watching_orientation) {
      readinessLabel = t('oralExam.lecturer.readiness.watchingOrientation', {
        defaultValue: 'Watching orientation',
      });
    } else if (readiness === OralExamReadiness.ready_to_start) {
      readinessLabel = t('oralExam.lecturer.readiness.readyToStart', {
        defaultValue: 'Ready to start',
      });
    } else {
      readinessLabel = t('oralExam.lecturer.readiness.inLobby', {
        defaultValue: 'In lobby — setting up',
      });
    }
    return {
      kind: 'approved',
      statusLabel: t('oralExam.lecturer.idReview.approved', { defaultValue: 'Approved' }),
      statusColor: '$statusSuccess',
      statusTestId: readinessStatusTestId(item.bookingId, readiness),
      tone: { dotColor: '$statusSuccess' },
      readinessLabel,
    };
  }

  if (readiness === OralExamReadiness.id_rejected) {
    const statusLabel = t('oralExam.lecturer.readiness.idRejected', {
      defaultValue: 'ID rejected — needs resubmit',
    });
    return {
      kind: 'rejected',
      statusLabel,
      statusColor: '$statusWarning',
      statusTestId: readinessStatusTestId(item.bookingId, readiness),
      tone: toneFromStatusColor('$statusWarning'),
    };
  }

  let statusLabel: string;
  let statusColor: string;

  if (readiness === OralExamReadiness.in_lobby && neitherConsentNorPhoto) {
    statusLabel = t('oralExam.lecturer.readiness.inLobby', {
      defaultValue: 'In lobby — setting up',
    });
    statusColor = '$textBody';
  } else if (readiness === OralExamReadiness.watching_orientation) {
    statusLabel = t('oralExam.lecturer.readiness.watchingOrientation', {
      defaultValue: 'Watching orientation',
    });
    statusColor = '$textBody';
  } else if (readiness === OralExamReadiness.ready_to_start) {
    statusLabel = t('oralExam.lecturer.readiness.readyToStart', {
      defaultValue: 'Ready to start',
    });
    statusColor = '$statusSuccess';
  } else {
    const consentLabel = hasConsent
      ? t('oralExam.lecturer.idReview.consentAccepted', {
        defaultValue: 'Consent accepted {{time}}',
        time: formatEnglishTime(item.consentRecordedAt as string),
      })
      : t('oralExam.lecturer.idReview.consentPending', { defaultValue: 'Consent not yet recorded' });
    const photoLabel = hasPhoto
      ? t('oralExam.lecturer.idReview.photoSubmitted', { defaultValue: 'ID photo submitted' })
      : t('oralExam.lecturer.idReview.awaitingPhoto', { defaultValue: 'Awaiting photo' });
    statusLabel = `${consentLabel} · ${photoLabel}`;
    statusColor = hasPhoto ? '$textBody' : '$statusWarning';
  }

  const showIdPhotos = shouldShowPreflightIdPhotos(readiness, item.idPhotoStatus);
  const kind: BandKind = showIdPhotos && hasPhoto ? 'review' : 'waiting';

  const tone = kind === 'review'
    ? { dotColor: '$statusWarning' }
    : toneFromStatusColor(statusColor);

  return {
    kind,
    statusLabel,
    statusColor,
    statusTestId: readinessStatusTestId(item.bookingId, readiness),
    tone,
  };
}
